import { UUID } from 'crypto';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';

// LEGACY PALETTE — DO NOT EDIT, REORDER, OR REMOVE ANY VALUE HERE.
// This is the exact 5-family x 6-shade (30 color) palette that shipped in production
// before this redesign. Existing cargoes may have a `colorId` that was explicitly
// assigned (or implicitly hash-derived) against this exact array; changing any value
// here would silently swap out a color a user already sees/chose, without their
// action. It is frozen forever and is never shown in the color picker or used for new
// assignments — it exists solely so `getCargoColor` can keep resolving old `colorId`
// values to the color the user originally saw. If the "current" palette below is ever
// redesigned again, repeat this pattern: freeze it as a new legacy array, fold it into
// `resolvedCargoColors`, and start a new "current" palette — never mutate a
// previously-shipped array in place.
const legacyReds = ['FF6961', 'FF827B', 'FF9A94', 'FFB3AE', 'FFCBC7', 'FFE4E1'];
const legacyOranges = ['FF8C00', 'FFA124', 'FFB648', 'FFCA6D', 'FFDB91', 'FFF4B5'];
const legacyGreens = ['20BA20', '44C843', '68D666', '8BE388', 'AFF1AB', 'D3FFCE'];
const legacyBlues = ['65CEEB', '7ED8EF', '96E2F3', 'AFEBF7', 'C7F5FB', 'E0FFFF'];
const legacyPurples = ['BA55D3', 'C06BD4', 'C680D5', 'CC96D6', 'D2ABD7', 'D8C1D8'];

const legacyCargoColors = [...legacyReds, ...legacyOranges, ...legacyGreens, ...legacyBlues, ...legacyPurples];

// CURRENT PALETTE
// This palette uses 12 hue families x 4 shades (48 colors total). Hues are spaced as
// evenly as the family names allow: most families sit ~25-30 degrees apart (computed
// directly in HSL), except Gold and Yellow, which are only ~20 degrees apart from each
// other and from Orange — "gold" (#FFD700, hue ~51) and "yellow" (#FFFF00, hue ~60) are
// inherently near-identical hues in color science, so forcing a full 30-degree gap
// between them would push one out of its recognizable range. They're told apart mainly
// by saturation/lightness instead (Gold: darker, more muted/amber; Yellow: brighter,
// more vivid) — the same technique already used to tell shades apart within a single
// family. Every color still meets a WCAG contrast ratio of at least 4.5:1 against the
// app's fixed text color, rgb(4, 38, 90) (`theme.colors.text.primary`).
//
// This is the palette used for the color picker UI and new manual color picks / the
// "randomize colors" bulk action. It is NOT used for the GUID-hash-based default color
// (that intentionally still comes from `legacyCargoColors` — see `getColorIndex` below)
// so that any cargo without an explicit pick keeps rendering exactly the color it
// always has. See `resolvedCargoColors` below for how old `colorId`s (assigned against
// the legacy palette above) are still resolved correctly.

// Reds (true red) — saturation spread widened (35% to 100%) since red's contrast floor
// against the app text color keeps lightness confined to a narrow ~0.62-0.66 band
// regardless of shade, making lightness alone insufficient to distinguish shades;
// leaning on saturation (muted/dusty to vivid) instead.
const reds = ['C17E7E', 'D97171', 'EE6262', 'FF5252'];

// Oranges
const oranges = ['D17A3C', 'D87631', 'E4813A', 'F7A164'];

// Golds
const golds = ['AB8D32', 'AD8C2B', 'CEA62C', 'E9C149'];

// Bright Yellows — inserted between Golds and Yellows. 3rd shade is the exact
// user-approved hex (A7A700 -> ... EEEE00); other shades are darker/lighter variants
// of the same hue/saturation, all independently verified >=4.5:1 contrast.
const brightYellows = ['A7A700', 'CACA00', 'EEEE00', 'FFFF22'];

// Yellows (true yellow)
const yellows = ['939D25', 'BCC926', 'D6E43A', 'EBF764'];

// Chartreuses
const chartreuses = ['00A700', '00CA00', '00EE00', '22FF22'];

// Greens
const greens = ['27A546', '26C94F', '3AE464', '64F789'];

// Cyans — inserted between Greens and Azures. 3rd shade is the exact user-approved hex
// (00EEEE); the other 3 shades are darker/lighter variants of the same hue/saturation,
// all independently verified >=4.5:1 contrast.
const cyans = ['00A7A7', '00CACA', '00EEEE', '22FFFF'];

// Azures
const azures = ['2D9BC0', '26A1C9', '3AB9E4', '64D2F7'];

// Purples — saturation spread widened (35% to 100%), same rationale as Reds: lightness
// is confined to a narrow band by the contrast floor, so leaning on saturation
// (muted/dusty to vivid) makes shades more distinguishable.
const purples = ['9288C6', '9384DE', '9380F1', '937DFF'];

// Magentas — saturation spread widened (35% to 100%), same rationale as Reds/Purples.
const magentas = ['AC80C2', 'B975DA', 'C26AEE', 'CA5FFF'];

// Roses (previously the "reds" family — renamed since it's actually a pink/rose-red,
// once a true "reds" family was added above). Saturation spread widened (35% to 100%),
// same rationale as Reds/Purples/Magentas.
const roses = ['C07C98', 'D86D9A', 'ED5B98', 'FF4794'];

export const cargoColors = [
    ...reds,
    ...oranges,
    ...golds,
    ...brightYellows,
    ...yellows,
    ...chartreuses,
    ...greens,
    ...cyans,
    ...azures,
    ...purples,
    ...magentas,
    ...roses,
];

// Append-only lookup used to resolve a persisted `colorId` to a hex value. Legacy
// indices (0..legacyCargoColors.length-1) are preserved exactly as they were in
// production; the current palette is appended after, so any `colorId` newly assigned
// (manual pick or randomize) always lands at an index that never collides with a
// legacy one. Only ever append future palettes here — never reorder.
const resolvedCargoColors = [...legacyCargoColors, ...cargoColors];

// Every newly-assigned colorId (manual pick, randomize) must be offset by this amount
// so it lands in the "current" segment of `resolvedCargoColors`, not the legacy one.
export const currentPaletteOffset = legacyCargoColors.length;

const getModularColorIndex = (id: number | undefined): number => {
    return id !== undefined ? id % legacyCargoColors.length : 0;
};

// IMPORTANT: this intentionally hashes against the frozen `legacyCargoColors` array,
// not the current `cargoColors` palette. Most cargoes never get an explicit `colorId`
// (it's only set via a manual pick or the "randomize colors" action) — they always
// rendered a color derived purely from this GUID hash. If this hashed into `cargoColors`
// instead, every such cargo's color would silently change the moment the "current"
// palette is redesigned, which is exactly the backward-compatibility problem this file
// exists to prevent. New cargoes only get a "current" palette color once something
// explicitly assigns them one (see `getNewRandomColorId`/`CargoColorPanel`).
export const getColorIndex = (cargoUuid: UUID): number => {
    const hexId = cargoUuid ? parseInt(cargoUuid.toString().slice(-6), 16) : undefined;
    return getModularColorIndex(hexId);
};

export const getNewRandomColorId = (): number => {
    return currentPaletteOffset + Math.floor(Math.random() * cargoColors.length);
};

// Also intentionally resolves against `legacyCargoColors` (see `getColorIndex` above) —
// this is the GUID-hash-based fallback color used whenever a cargo has no explicit
// `colorId`, and must keep producing the exact same color it always has.
export const getColorById = (cargoUuid: UUID): string | undefined => {
    const colorIndex = getColorIndex(cargoUuid);
    return cargoUuid ? legacyCargoColors[colorIndex] : undefined;
};

export const getCargoColor = (cargo: RotationCargo): string | undefined => {
    if (cargo.colorId !== null && cargo.colorId !== undefined && cargo.colorId >= 0 && cargo.colorId < resolvedCargoColors.length) {
        return resolvedCargoColors[cargo.colorId];
    }
    return getColorById(cargo.cargoGuid);
};

// Groups the given cargoes by their effective displayed color (whatever `getCargoColor`
// would resolve to right now — an explicit pick, or the GUID-hash fallback), so callers
// (the color picker) can inform users which colors are already in use elsewhere in the
// same stowage plan without blocking them from picking a color again. `excludeCargoGuid`
// is normally the guid of the cargo currently being edited, so its own current color
// isn't reported as "used by someone else".
export const getUsedCargoColors = (cargoes: RotationCargo[], excludeCargoGuid?: UUID): Map<string, RotationCargo[]> => {
    const usedColors = new Map<string, RotationCargo[]>();

    cargoes
        .filter((cargo) => cargo.cargoGuid !== excludeCargoGuid)
        .forEach((cargo) => {
            const color = getCargoColor(cargo);
            if (!color) {
                return;
            }

            const cargoesForColor = usedColors.get(color);
            if (cargoesForColor) {
                cargoesForColor.push(cargo);
            } else {
                usedColors.set(color, [cargo]);
            }
        });

    return usedColors;
};
